import { HttpClient } from '@angular/common/http';
import { Component, computed, ElementRef, inject, OnInit, signal, ViewChild } from '@angular/core';

import { Sort, SortDirection } from '@angular/material/sort';

import { translate } from '@jsverse/transloco';
import { saveAs } from 'file-saver-es';
import { toCanvas } from 'html-to-image';

import { firstValueFrom } from 'rxjs';

import { Constants } from 'app/constants/constants';
import { DownloadAsDialogResult } from 'app/modules/collection/components/download-as-dialog/download-as-dialog.component';
import {
  Filter,
  FilterService,
  WidgetQueryOptions,
} from 'app/modules/collection/services/filter.service';
import { DashboardViewComponent } from 'app/modules/dashboard/pages/dashboard-view/dashboard-view.component';
import { CustomFieldsService } from 'app/modules/field/custom-fields.service';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import {
  Comment,
  CommentPoint,
  Comparison,
  WidgetDefault,
} from 'app/providers/bridge-api/dashboard.service';
import { StorageAccountService } from 'app/providers/storage-api/blender-account.service';
import {
  DocumentFacetListResult,
  DocumentService,
  SearchOptions,
} from 'app/providers/storage-api/document.service';
import { FieldService } from 'app/providers/storage-api/field.service';
import { InteractiveSmartSummaryService } from 'app/providers/storage-api/interactive-smart-summary.service';
import { ProjectService } from 'app/providers/storage-api/project.service';
import { encodeParams, Report, ReportService } from 'app/providers/storage-api/report.service';
import { DownloadTasksService, TaskType } from 'app/providers/tasks-api/download.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService } from 'app/services/dialog.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { SystemService } from 'app/services/system.service';
import { TimezoneService } from 'app/services/timezone.service';
import { WidgetLimiterService } from 'app/services/widget-limiter.service';
import { StringBuilder } from 'app/utils/string-builder';
import { Utils } from 'app/utils/utils';
import { WidgetDataSource } from '../../../../models';
import { TimeIntervalPipe } from '../../../../pipes';
import { ComparisonHandlerService } from '../../../dashboard/services/comparison-handler-service';
import { GridBreakdownTaxonomyConfig } from '../grid-analytic-item-selector-dialog/grid-breakdown-taxonomy-config/grid-breakdown-taxonomy-config.component';
import { NumericMetadataField } from '../numeric-metadata-field-selector/numeric-metadata-field-selector.models';
import { getDefaultDecimalPrecision } from '../numeric-metadata-field-selector/numeric-metadata-field-selector.utils';
import { AbstractTableWidgetComponent } from '../widget/abstract-table-widget.component';
import { WidgetComponent } from '../widget/widget.component';

import { ChartHelper } from '../../services/chart-helper';
import { PptxReport } from '../../services/pptx.service';
import { WidgetCommentsHandlerService } from '../../services/widget-comments-handler.service';

export interface WidgetGridOptions extends WidgetDefault {
  breakdownField: string;
  breakdownLabel: string;
  displayField: string;
  includeBlank: boolean;
  showAll: boolean;
  allLabel: string;
  showTopN: boolean;
  topNLabel: string;
  sort: GridSort;
  numericMetadataFields: NumericMetadataField[];
  dateInterval?: string;
  showDeltaColumn?: boolean;
  selectedComparisonMetadataFieldIndex?: number;
  taxonomyOptions?: GridBreakdownTaxonomyConfig;
}

interface GridSort {
  field: string;
  dir: SortDirection;
}

export interface GridTableColumns {
  [def: string]: GridTableColumn;
}

interface GridTableColumn {
  label: string;
  pipe?: string;
  tooltip?: string;
}

type GridDataItemType = 'total' | 'normal';

type GridReport = Report<GridReportItems>;

interface GridReportItems {
  [breakdown: string]: GridReportItem;
  data?: GridReportItem;
}

interface GridReportItem {
  metadata: GridReportMetadata[];
}

type TotalType = 'TOPN' | 'ALL';

export interface GridReportMetadata {
  score?: number;
  topScore?: number;
  bottomScore?: number;
  docs: number;
  field: string;
  scoreType: string;
  totalType?: TotalType;
  sentimentPolarity?: any;
  buckets?: GridReportMetadataBucket[];
}

export interface GridReportMetadataBucket {
  count: number;
  label: string;
}

interface GridDataItemBase {
  breakdown: string;
  breakdownDisplay: string;
  type: GridDataItemType;
  documentCount?: number;
}
interface GridDataFields {
  [fieldId: string]: string | number | null;
}

type GridDataItem = GridDataItemBase & Omit<GridDataFields, keyof GridDataItemBase>;

const TOTAL_REPORT_ITEM = 'data';

@EnableClassTelemetry()
@Component({
  selector: 'app-widget-grid',
  templateUrl: './widget-grid.component.html',
  styleUrls: ['./widget-grid.component.scss'],
  standalone: false,
  host: {
    '(document:click)': 'closeComparisonMetadataDropdown()',
  },
})
export class WidgetGridComponent
  extends AbstractTableWidgetComponent<WidgetGridOptions>
  implements OnInit
{
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @ViewChild('table', { read: ElementRef }) table: ElementRef<HTMLTableElement>;

  public hasAnalyses = true;
  public canFilter = true;
  public downloadFormats: string[] = ['pptx', 'image', 'csv'];

  dataSource: GridDataItem[] = [];
  columns: GridTableColumns;
  columnsForExport: GridTableColumns = {};
  displayedColumns: string[];
  displayedColumnsWithComment: string[] = [];

  pointName: string;
  selectedParentFilterBreakdown: string;
  hasWidgetGroups;
  canAddFilterToWidgetGroup;
  showAddToWidgetGroupModal;

  noDocuments;
  downloadingSpoteRequest?: boolean;
  disablePinnedCommentClick = false;
  timeIntervalPipe = inject(TimeIntervalPipe);

  private readonly comparisonHandlerService = inject(ComparisonHandlerService);

  comparisonMetadataDropdownOpen = false;
  readonly comparisonMetadataSearch = signal('');
  readonly filteredComparisonMetadataFields = computed(() => {
    const search = this.comparisonMetadataSearch().toLowerCase().trim();
    const fields = this.payload.numericMetadataFields ?? [];
    if (!search) {
      return fields;
    }
    return fields.filter((f) => f.label?.toLowerCase().includes(search));
  });
  hasDocumentsColumn = false;

  constructor(
    private reportService: ReportService,
    private httpClient: HttpClient,
    private downloadService: DownloadTasksService,
    protected systemService: SystemService,
    protected accountService: StorageAccountService,
    protected authService: AuthService,
    protected timezoneService: TimezoneService,
    protected filterService: FilterService,
    protected alertService: AlertService,
    protected interactiveSmartSummaryService: InteractiveSmartSummaryService,
    protected dialogService: DialogService,
    protected widgetLimiterService: WidgetLimiterService,
    protected documentService: DocumentService,
    protected projectService: ProjectService,
    protected dashboardViewComponent: DashboardViewComponent,
    protected widgetComponent: WidgetComponent,
    protected customFieldService: CustomFieldsService,
    protected commentsHandlerService: WidgetCommentsHandlerService
  ) {
    super(
      filterService,
      dialogService,
      documentService,
      projectService,
      alertService,
      widgetLimiterService,
      interactiveSmartSummaryService,
      dashboardViewComponent,
      widgetComponent,
      systemService,
      accountService,
      authService,
      timezoneService,
      customFieldService,
      commentsHandlerService
    );
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.setColumns();

    this.refresh(false, true);
  }

  public get defaultTitle(): string {
    return translate('widgets.title.grid');
  }

  public override canApplyComparison(_comparison?: Comparison): boolean {
    if (this.payload.transpose) {
      return false;
    }

    return true;
  }

  public override getComparisonErrorMessage(comparison?: Comparison): string | undefined {
    if (this.payload.transpose) {
      return 'widgets.comparison-grid-transposed';
    }

    if (comparison && !this.canApplyComparison(comparison)) {
      return 'widgets.has-invalid-comparison';
    }
  }

  public override isComparisonChangeDisabled(): boolean {
    return !!this.dashboard.locked;
  }

  get isComparisonMode(): boolean {
    return !!this.comparison;
  }

  get canChangeComparisonMetadataField(): boolean {
    return (this.payload.numericMetadataFields?.length ?? 0) > 1;
  }

  get comparisonFilterCount(): number {
    return this.comparison?.filters?.length ?? 0;
  }

  get canShowDelta(): boolean {
    return this.isComparisonMode && this.comparisonFilterCount === 2;
  }

  get showDeltaColumn(): boolean {
    return this.canShowDelta && (this.payload.showDeltaColumn ?? true);
  }

  get selectedComparisonMetadataField(): NumericMetadataField {
    const idx = this.payload.selectedComparisonMetadataFieldIndex ?? 0;
    return this.payload.numericMetadataFields?.[idx] ?? this.payload.numericMetadataFields?.[0];
  }

  get isNoBreakdown(): boolean {
    return (
      !this.payload.breakdownField || this.payload.breakdownField === Constants.NO_BREAKDOWN_VALUE
    );
  }

  selectComparisonMetadataField(index: number): void {
    this.payload.selectedComparisonMetadataFieldIndex = index;
    this.closeComparisonMetadataDropdown();
    this.refresh();
  }

  toggleComparisonMetadataDropdown(): void {
    if ((this.payload.numericMetadataFields?.length ?? 0) < 2) {
      return;
    }
    this.comparisonMetadataDropdownOpen = !this.comparisonMetadataDropdownOpen;
    if (this.comparisonMetadataDropdownOpen) {
      this.comparisonMetadataSearch.set('');
    }
  }

  closeComparisonMetadataDropdown(): void {
    this.comparisonMetadataDropdownOpen = false;
    this.comparisonMetadataSearch.set('');
  }

  onComparisonMetadataSearchChange(value: string): void {
    this.comparisonMetadataSearch.set(value);
  }

  public canShowDataTable(): boolean {
    return false;
  }

  public async refresh(
    reset?: boolean,
    initialLoading?: boolean,
    refreshedDatasource?: WidgetDataSource,
    _isDataSourceChanged?: boolean
  ): Promise<void> {
    super.refresh(reset, initialLoading, refreshedDatasource);

    this.loadingStarted.emit(initialLoading);
    this.isLoading = true;

    this.selectedParentFilterBreakdown = undefined;

    this.ensureSelectedComparisonMetadataField();

    this.setColumns();

    if (this.isFilterGroupParent) {
      const parentFilters = this.widgetGroup.parentFilters.metadata.and;
      if (parentFilters.length) {
        this.selectedParentFilterBreakdown = parentFilters[0].value;
      }
    }

    this.hasWidgetGroups = this.dashboard.widgetGroups
      ? Object.getOwnPropertyNames(this.dashboard.widgetGroups).length > 0
      : false;

    this.dataSourceNotFound ||= !this.widgetDataSource;

    if (!this.dataSourceNotFound) {
      if (this.isComparisonMode) {
        const reports = await this.getComparisonReports();
        this.filteredDocumentCount = reports[0]?.totalHits ?? 0;
        this.processComparisonReports(reports);
      } else {
        const gridReport = (await this.getReport()) as GridReport;
        this.filteredDocumentCount = gridReport.totalHits;
        this.processReport(gridReport);
      }
    }

    this.loadingFinished.emit(initialLoading);
    this.isLoading = false;
  }

  private getParams() {
    const isTaxonomy = this.payload.breakdownField === 'taxonomy';
    const isCustomTaxonomy = this.payload.taxonomyOptions?.customItemsSelected;

    const params: any = {
      analysisName: this.widgetDataSource.analysisId,
      timezone: this.timezoneService.timezone,
      totalRow: this.hasTotalRow,
      breakdownField: this.payload.breakdownField,
      breakdownSize: Constants.GRID_TOP_N,
      includeBlank: this.payload.includeBlank,
      isSocial: this.widgetDataSource.isSocial,
      breakdownInterval: this.payload.dateInterval,
      polarityScale: this.dashboard.payload.sentimentScale,
    };

    if (this.isNoBreakdown) {
      params.breakdownField = undefined;
    } else {
      if (isTaxonomy) {
        if (isCustomTaxonomy) {
          params.featureTypes = 'taxonomy';
          params.selectedItems = this.payload.taxonomyOptions?.customTaxonomyItems;
        } else {
          params.breakdownSize = this.payload.taxonomyOptions?.showTopTaxonomyNum;
          params.leafNodesOnly = this.payload.taxonomyOptions?.leafNodesOnly;
        }
      }

      if (!FieldService.isCustomField(this.payload.breakdownField)) {
        params.breakdownField =
          FieldService.STANDARD_FIELDS_LABEL_TO_ID[this.payload.breakdownField] ||
          this.payload.breakdownField;
      }
    }

    if (this.payload.weightField) {
      params.weightField = this.payload.weightField;
    }

    if (
      this.payload.numericMetadataFields.some(
        (f) => f.fieldId === 'sentimentScore' && f.scoring !== 'avg'
      )
    ) {
      params.fetchPolarityData = true;
    }

    return params;
  }

  public getMapData(includeLabels = false): { [key: string]: string }[] {
    const body: { [key: string]: string }[] = this.payload.numericMetadataFields.map((f) =>
      this.mapNumericMetadataFieldToBodyParam(f, includeLabels)
    );

    if (this.payload.displayField) {
      const breakdown: any = {
        scoringStrategy: 'COMMON_VALUES',
      };

      if (FieldService.isCustomField(this.payload.displayField)) {
        breakdown.customField = this.payload.displayField;
      } else {
        breakdown.standardField =
          FieldService.STANDARD_FIELDS_LABEL_TO_ID[this.payload.displayField] ||
          this.payload.displayField;
      }

      if (includeLabels) {
        breakdown.label = this.payload.breakdownLabel;
      }
      // Don't add 'COMMON_VALUES' breakdown if it's taxonomy
      if (breakdown.standardField === 'taxonomy') {
        return body;
      }

      body.push(breakdown);
    }

    return body;
  }

  private async getComparisonReports(): Promise<GridReport[]> {
    return Promise.all(
      this.comparison.filters.map((filterSet) => {
        const params = this.getParams();
        if (this.isNoBreakdown) {
          params.breakdownField = undefined;
          params.totalRow = true;
        }
        const body = this.getComparisonMetadataBody();
        const queryOptions = this.filterService.getWidgetQueryOptions(
          this.widget,
          this.widgetDataSource,
          this.widgetGroup,
          filterSet
        );
        const query = this.filterService.getQueryForDocuments(queryOptions, false, true);
        return this.reportService.postReport(
          this.widgetDataSource.collectionName,
          this.widgetDataSource.groupId,
          'documentNumericMetadata',
          params,
          body,
          'json',
          query,
          undefined,
          undefined,
          this.payload.projectId
        ) as Promise<GridReport>;
      })
    );
  }

  private getComparisonMetadataBody(): { [key: string]: string }[] {
    if (this.isNoBreakdown) {
      return this.payload.numericMetadataFields.map((f) =>
        this.mapNumericMetadataFieldToBodyParam(f)
      );
    }
    const field = this.selectedComparisonMetadataField;
    if (!field) {
      return [];
    }
    const breakdown: any = { scoringStrategy: 'COMMON_VALUES' };
    if (!FieldService.isCustomField(this.payload.displayField)) {
      breakdown.standardField =
        FieldService.STANDARD_FIELDS_LABEL_TO_ID[this.payload.displayField] ||
        this.payload.displayField;
    } else {
      breakdown.customField = this.payload.displayField;
    }
    if (breakdown?.standardField === 'taxonomy') {
      return [this.mapNumericMetadataFieldToBodyParam(field)];
    }
    return [this.mapNumericMetadataFieldToBodyParam(field), breakdown];
  }

  private processComparisonReports(reports: GridReport[]): void {
    this.setColumns();

    if (this.isNoBreakdown) {
      this.processComparisonNoBreakdown(reports);
      return;
    }

    const allBreakdowns = new Set<string>();
    reports.forEach((r) =>
      Object.keys(r.items)
        .filter((k) => k !== TOTAL_REPORT_ITEM)
        .forEach((k) => allBreakdowns.add(k))
    );

    const field = this.selectedComparisonMetadataField;
    const decimal = field?.decimals ?? getDefaultDecimalPrecision(field?.fieldId);
    const pipe = `1.${decimal}-${decimal}`;

    const filterNames = this.comparison.filters.map((f, i) => {
      const label = this.comparisonFilterDisplayName(f, i);
      return `${field?.label ?? ''} (${label})`;
    });

    this.columns = { breakdown: { label: this.payload.breakdownLabel } };
    reports.forEach((_, i) => {
      const colKey = `comparison_${i}`;
      this.columns[colKey] = { label: filterNames[i], pipe };
    });

    if (this.showDeltaColumn) {
      this.columns['delta'] = { label: translate('widgets.grid.delta'), pipe };
    }

    this.displayedColumns = Object.keys(this.columns);
    this.updateColumnsWithComments();

    const dataItems: GridDataItem[] = [];
    for (const breakdown of allBreakdowns) {
      const item: GridDataItem = {
        breakdown,
        breakdownDisplay: this.getComparisonBreakdownDisplay(breakdown, reports[0]),
        type: 'normal',
      };
      reports.forEach((r, i) => {
        const metadata = r.items[breakdown]?.metadata?.[0];
        item[`comparison_${i}`] = this.extractScore(metadata, field) ?? null;
      });
      if (this.showDeltaColumn) {
        item['delta'] = this.calculateDelta(
          item['comparison_0'] as number | undefined,
          item['comparison_1'] as number | undefined
        );
      }
      dataItems.push(item);
    }

    this.applySorting(dataItems);
    this.widgetComponent.updateWidgetActionComments();
  }

  private processComparisonNoBreakdown(reports: GridReport[]): void {
    const dataItems: GridDataItem[] = [];

    const filterNames = this.comparison.filters.map((f, i) =>
      this.comparisonFilterDisplayName(f, i)
    );

    this.columns = { breakdown: { label: translate('widgets.grid.numeric-metadata') } };
    reports.forEach((_, i) => {
      this.columns[`comparison_${i}`] = { label: filterNames[i] };
    });

    if (this.showDeltaColumn) {
      this.columns['delta'] = { label: translate('widgets.grid.delta'), pipe: '1.2-2' };
    }

    this.displayedColumns = Object.keys(this.columns);
    this.updateColumnsWithComments();

    for (const field of this.payload.numericMetadataFields) {
      const decimal = field.decimals ?? getDefaultDecimalPrecision(field.fieldId);
      const item: GridDataItem = {
        breakdown: field.fieldId,
        breakdownDisplay: field.label,
        type: 'normal',
      };
      reports.forEach((r, i) => {
        const totalData = r.items?.[TOTAL_REPORT_ITEM];
        const metadata = totalData?.metadata?.find(
          (m) => m.field === field.fieldId || (field.fieldId === 'docs' && m.field === 'documents')
        );
        item[`comparison_${i}`] = this.extractScore(metadata, field) ?? null;
        item['pipe'] = `1.${decimal}-${decimal}`;
      });
      if (this.showDeltaColumn) {
        item['delta'] = this.calculateDelta(
          item['comparison_0'] as number | undefined,
          item['comparison_1'] as number | undefined
        );
      }
      dataItems.push(item);
    }

    this.dataSource = dataItems;
    this.widgetComponent.updateWidgetActionComments();
  }

  private extractScore(
    metadata: GridReportMetadata | undefined,
    field: NumericMetadataField
  ): number | undefined {
    if (!metadata) {
      return undefined;
    }
    switch (field?.scoring) {
      case 'top':
        return metadata.topScore;
      case 'bottom':
        return metadata.bottomScore;
      default:
        return metadata.score;
    }
  }

  private calculateDelta(a: number | undefined, b: number | undefined): number | null {
    if (a == null || b == null || isNaN(a as any) || isNaN(b as any)) {
      return null;
    }
    return (b as number) - (a as number);
  }

  private getBreakdownDisplay(report: GridReport, breakdown: string): string {
    const helper = new ChartHelper();
    return (
      helper.getTopAssociatedLabel(report.items[breakdown].metadata, breakdown) || Constants.BLANK
    );
  }

  private comparisonFilterDisplayName(filter: any, index: number): string {
    try {
      const extractor = this.comparisonHandlerService.filterDisplayValueExtractorFn(
        this.comparison.type
      );
      const label = extractor(filter);
      if (label) {
        return label;
      }
    } catch {
      // fall through to index fallback
    }
    return `${index + 1}`;
  }

  private async getReport(): Promise<Report<{ [key: string]: any }>> {
    const params = this.getParams();
    const body = this.getMapData();

    return this.reportService.postReport(
      this.widgetDataSource.collectionName,
      this.widgetDataSource.groupId,
      'documentNumericMetadata',
      params,
      body,
      'json',
      this.filterService.getQueryForDocuments(this.widgetQueryOptions, false, true),
      undefined,
      undefined,
      this.payload.projectId
    ) as Promise<Report<{ [key: string]: any }>>;
  }

  private mapNumericMetadataFieldToBodyParam(
    numericMetadataField: NumericMetadataField,
    includeLabels: boolean = false
  ): {
    [key: string]: string;
  } {
    if (numericMetadataField.fieldId === 'sentimentScore') {
      return {
        standardField: 'sentimentScore',
        scoringStrategy: 'AVERAGE',
      };
    }

    const result = {
      customField: numericMetadataField.fieldId,
    };
    if (includeLabels) {
      result['label'] = numericMetadataField.label;
    }

    switch (numericMetadataField.scoring) {
      case 'avg':
        result['scoringStrategy'] = 'AVERAGE';
        break;
      case 'sum':
        result['scoringStrategy'] = 'SUM';
        if (numericMetadataField.fieldId === 'docs') {
          result['scoringStrategy'] = 'COUNT';
          result.customField = 'Documents';
        }
        break;
      case 'top':
      case 'bottom':
        result['scoringStrategy'] = 'BOX';
        result['scoreRange'] = `${numericMetadataField.min}:${numericMetadataField.max}`;
        result['boxDirection'] = numericMetadataField.scoring === 'top' ? 'TOP' : 'BOTTOM';
        result['boxSize'] = numericMetadataField.boxSize;
        break;
      case 'nps':
        result['scoringStrategy'] = 'NPS';
        result['scoreRange'] = `${numericMetadataField.min}:${numericMetadataField.max}`;
        break;
      case 'percentage':
        result['scoringStrategy'] = 'PERCENTAGE';
        if (numericMetadataField.fieldId === 'docs') {
          result.customField = 'Documents';
        }

        break;
    }

    return result;
  }

  private processReport(report: GridReport): void {
    this.noDocuments =
      this.filteredDocumentCount === 0 ||
      !Object.values(report.items).some((data) =>
        data.metadata.some((metadata) => metadata.docs !== 0)
      );

    const dataItems: GridDataItem[] = [];

    if (!this.payload.transpose) {
      dataItems.push(...this.getBreakdownsFromReport(report));
      dataItems.push(...this.getTotalFromReport(report));
    } else {
      dataItems.push(...this.getTransposedFieldsAndBreakdownsFromReport(report));
    }
    this.hasDocumentsColumn = this.payload.numericMetadataFields.some((f) => f.fieldId === 'docs');

    this.applySorting(dataItems);

    this.widgetComponent.updateWidgetActionComments();
  }

  private getTransposedFieldsAndBreakdownsFromReport(report: GridReport) {
    const result = [];
    const fields = Object.getOwnPropertyNames(report.items).sort((a, b) => {
      if (a === TOTAL_REPORT_ITEM) {
        return -1;
      }

      if (b === TOTAL_REPORT_ITEM) {
        return 1;
      }

      return a.toLocaleLowerCase().localeCompare(b.toLocaleLowerCase());
    });

    this.columns = {
      [Constants.TRANSPOSED_METADATA_COLUMN]: { label: translate('widgets.grid.numeric-metadata') },
    };
    this.payload.numericMetadataFields.forEach((field) => {
      const decimal = field.decimals ?? getDefaultDecimalPrecision(field.fieldId);
      const getScoreField = (f: NumericMetadataField): string => {
        switch (f.scoring) {
          case 'top':
            return 'topScore';
          case 'bottom':
            return 'bottomScore';
          case 'percentage':
            return 'percentage';
          default:
            return 'score';
        }
      };
      const rowData: any = {
        [Constants.TRANSPOSED_METADATA_COLUMN]: field.fieldId,
        label: field.label,
        pipe: `1.${decimal}-${decimal}`,
        precision: +decimal,
        type: 'normal',
        scoreField: getScoreField(field),
      };
      for (const breakdown of fields) {
        const metadata = report.items[breakdown].metadata;
        const metadataForField = metadata.find(
          (md) =>
            md.field === field.fieldId ||
            (field.fieldId === 'docs' && md.field === 'documents') ||
            (field.fieldId === 'sentimentScore' && md.field === 'analyses.sentimentScore')
        );
        rowData[breakdown] = metadataForField;
        if (metadataForField.field === 'analyses.sentimentScore') {
          rowData.sentimentScoring = field.scoring;

          if (field.scoring === 'percentage') {
            rowData.percentageSentiment = field.percentageSentiment;
            rowData[breakdown].percentage =
              (metadataForField.sentimentPolarity.buckets[field.percentageSentiment] /
                metadataForField.docs) *
              100;
          }
        }

        const getColumnLabel = (bd: string) => {
          if (bd !== TOTAL_REPORT_ITEM) {
            if (this.payload.dateInterval) {
              return this.getFormattedBreakdownDisplay(bd);
            }
            return bd;
          }

          if (this.payload.showAll) {
            return this.payload.allLabel;
          }

          if (this.payload.showTopN) {
            return this.payload.topNLabel;
          }
        };
        const columnLabel = getColumnLabel(breakdown);
        this.columns[breakdown] = { label: columnLabel };
        this.columnsForExport[breakdown] = { label: columnLabel };
      }
      result.push(rowData);
    });
    this.displayedColumns = Object.keys(this.columns);
    this.displayedColumnsWithComment = Object.keys(this.columnsForExport);
    return result;
  }

  private getBreakdownsFromReport(report: GridReport): GridDataItem[] {
    const result: GridDataItem[] = [];

    const breakdowns = Object.getOwnPropertyNames(report.items).filter(
      (i) => i !== TOTAL_REPORT_ITEM
    );
    for (const breakdown of breakdowns) {
      const metadata = report.items[breakdown].metadata;
      const helper = new ChartHelper();

      const breakdownDisplay = helper.getTopAssociatedLabel(metadata, breakdown) || Constants.BLANK;

      result.push(this.createDataItem(metadata, breakdown, breakdownDisplay, 'normal'));
    }

    return result;
  }

  private getTotalFromReport(report: GridReport): GridDataItem[] {
    if (!this.hasTotalRow) {
      return [];
    }

    const result: GridDataItem[] = [];

    const metadata = report.items.data.metadata;

    if (this.payload.showAll) {
      result.push(this.createTotalDataItem(metadata, 'ALL', this.payload.allLabel));
    }

    if (this.payload.showTopN) {
      result.push(this.createTotalDataItem(metadata, 'TOPN', this.payload.topNLabel));
    }

    return result;
  }

  private createTotalDataItem(
    metadata: GridReportMetadata[],
    totalType: TotalType,
    label: string
  ): GridDataItem {
    const metadataByTotalType = metadata.filter((m) => m.totalType === totalType);

    return this.createDataItem(metadataByTotalType, TOTAL_REPORT_ITEM, label, 'total');
  }

  private createDataItem(
    metadata: GridReportMetadata[],
    breakdown: string,
    breakdownDisplay: string,
    type: GridDataItemType
  ): GridDataItem {
    let newBreakdownDisplay = breakdownDisplay;
    if (this.payload.dateInterval) {
      newBreakdownDisplay = this.getFormattedBreakdownDisplay(breakdownDisplay);
    }
    const dataItem: GridDataItem = {
      breakdown,
      breakdownDisplay: newBreakdownDisplay,
      type,
    };

    this.setScoresToDataItem(dataItem, metadata);

    return dataItem;
  }

  private getComparisonBreakdownDisplay(breakdown: string, firstReport: GridReport): string {
    if (this.payload.dateInterval) {
      return this.getFormattedBreakdownDisplay(breakdown);
    }
    return firstReport?.items[breakdown]
      ? this.getBreakdownDisplay(firstReport, breakdown)
      : breakdown;
  }

  private getFormattedBreakdownDisplay(breakdownDisplay: string): string {
    return this.timeIntervalPipe.transform(breakdownDisplay, this.payload.dateInterval);
  }

  private setScoresToDataItem(dataItem: GridDataItem, metadata: GridReportMetadata[]): void {
    for (let index = 0; index < this.payload.numericMetadataFields.length; index++) {
      const numericMetadataField = this.payload.numericMetadataFields[index];
      const values = metadata[index];
      if (!values) {
        continue;
      }
      const column = `${numericMetadataField.fieldId}_${index}`;
      dataItem.documentCount = values.docs;
      dataItem[column + '_scoring'] = numericMetadataField.scoring;

      let score;
      switch (numericMetadataField.scoring) {
        case 'top':
          score = values.topScore;
          break;
        case 'bottom':
          score = values.bottomScore;
          break;
        case 'sum':
          // DASH-9952: Scores for <blank> documents are left out in every other column (totals as well),
          // but it should show the document count for the <blank> row, because the user want to know that.
          const useDocCountScore: boolean =
            numericMetadataField.fieldId === 'docs' &&
            Utils.unescapeBlank(dataItem.breakdown) === Constants.BLANK;
          score = useDocCountScore ? values.docs : values.score;
          break;
        case 'percentage':
          if (numericMetadataField.fieldId === 'sentimentScore') {
            dataItem[column + '_sentiment'] = numericMetadataField.percentageSentiment;
            score =
              (values.sentimentPolarity.buckets[numericMetadataField.percentageSentiment] /
                values.docs) *
              100;
          } else {
            score = values.score;
          }

          break;
        case 'sentimentBar':
          dataItem.sentimentBuckets = values.sentimentPolarity.buckets;
          break;
        default:
          score = values.score;
          break;
      }

      if (isNaN(score) || score === 'Infinity') {
        continue;
      }

      dataItem[column] = score;
    }
  }

  onSortChange(event: Sort): void {
    this.payload.sort.field = event.active;
    this.payload.sort.dir = event.direction;

    this.applySorting(this.dataSource);
  }

  getColumnHeader(def: string): string {
    return this.columns[def]?.label || this.columnsForExport[def]?.label;
  }

  getDecimalPrecisionPipe(column: string): string | undefined {
    return this.columns[column]?.pipe;
  }

  private applySorting(items: GridDataItem[]): void {
    const sorting = (a: GridDataItem, b: GridDataItem) => {
      const dir = this.payload.sort.dir === 'asc' ? 1 : -1;

      if (this.payload.sort.field === 'breakdown') {
        if (this.payload.dateInterval) {
          const aAsDate = new Date(a.breakdown);
          const bAsDate = new Date(b.breakdown);
          return (aAsDate.getTime() - bAsDate.getTime()) * dir;
        }
        return (
          a.breakdownDisplay
            .toLocaleLowerCase()
            .localeCompare(b.breakdownDisplay.toLocaleLowerCase()) * dir
        );
      }
      if (this.payload.sort.field === Constants.TRANSPOSED_METADATA_COLUMN) {
        return (
          (a.label as string)
            .toLocaleLowerCase()
            .localeCompare((b.label as string).toLocaleLowerCase()) * dir
        );
      }

      if (a[this.payload.sort.field] == null) {
        return 1 * dir;
      }
      if (b[this.payload.sort.field] == null) {
        return -1 * dir;
      }
      const valA = !this.payload.transpose
        ? a[this.payload.sort.field]
        : a[this.payload.sort.field][a.scoreField];
      const valB = !this.payload.transpose
        ? b[this.payload.sort.field]
        : b[this.payload.sort.field][b.scoreField];
      return (parseFloat(valA) - parseFloat(valB)) * dir;
    };
    const sortedTotalItems = items.filter((i) => i.type === 'total').sort(sorting);
    const sortedItems = items.filter((i) => i.type === 'normal').sort(sorting);

    this.dataSource = [...sortedTotalItems, ...sortedItems];
  }

  private setColumns(): void {
    if (this.isComparisonMode) {
      return;
    }

    if (!this.payload.transpose) {
      this.columns = {
        breakdown: { label: this.payload.breakdownLabel ?? '' },
      };

      this.payload.numericMetadataFields.forEach((f, i) => {
        const decimal = f.decimals ?? getDefaultDecimalPrecision(f.fieldId);
        const value = {
          label: f.label,
          pipe: `1.${decimal}-${decimal}`,
          precision: +decimal,
          tooltip: this.formatColumnTooltip(f),
        };

        this.columns[`${f.fieldId}_${i}`] = value;
        this.columnsForExport[`${f.fieldId}_${i}`] = value;
      });
    } else {
      this.columns ??= {
        [Constants.TRANSPOSED_METADATA_COLUMN]: {
          label: translate('widgets.grid.numeric-metadata'),
        },
      };
    }

    this.displayedColumns = Object.keys(this.columns);
    this.updateColumnsWithComments();
  }

  private updateColumnsWithComments(): void {
    this.columnsForExport = {
      ...this.columns,
      ...(this.widget.payload?.comments?.some((c) => c.titlePinned || c.summaryPinned) && {
        comments: this.columns['comments'],
      }),
    };
    this.displayedColumnsWithComment = Object.keys(this.columnsForExport);
  }

  private formatColumnTooltip(numericMetadataField: NumericMetadataField): string {
    if (numericMetadataField.fieldId === 'docs') {
      return this.payload.includeBlank
        ? translate('widgets.grid.includes-blanks')
        : translate('widgets.grid.excludes-blanks');
    }

    const sb = new StringBuilder();

    sb.appendLine(
      translate('widgets.grid.field', {
        field: FieldService.getFieldLongName(numericMetadataField.fieldId),
      })
    );

    if (numericMetadataField.scoring === 'top' || numericMetadataField.scoring === 'bottom') {
      sb.appendLine(translate('widgets.grid.min', { min: numericMetadataField.min }));
      sb.appendLine(translate('widgets.grid.max', { max: numericMetadataField.max }));
    }

    sb.append(
      translate('widgets.grid.scoring', {
        scoring: this.formatScoringTooltip(numericMetadataField),
      })
    );

    return sb.toString();
  }

  private formatScoringTooltip(numericMetadataField: NumericMetadataField): string {
    switch (numericMetadataField.scoring) {
      case 'avg':
        return translate('widgets.grid.avg');
      case 'sum':
        return translate('widgets.grid.sum');
      case 'nps':
        return translate('widgets.grid.nps');
      case 'top':
      case 'bottom':
        return translate('widgets.grid.box', {
          top: numericMetadataField.scoring === 'top',
          size: numericMetadataField.boxSize,
        });
      case 'percentage':
        const sentiment = translate(
          `project-sentiment-range.polarities.${numericMetadataField.percentageSentiment.replace(' ', '-')}`
        );
        return translate('widgets.grid.percentage', { sentiment });
      case 'sentimentBar':
        return translate('widgets.grid.sentimentBar');
    }
  }

  async showDocumentsForItem(): Promise<void> {
    const query = this.filterService.getQueryForDocuments(this.widgetQueryOptions);

    let page: DocumentFacetListResult | undefined;

    try {
      const searchOptions: SearchOptions = {
        timezone: this.timezoneService.timezone,
      };

      page = await this.documentService.searchForDocuments(
        this.widgetDataSource,
        this.dashboard.project_id,
        this.widgetDataSource.collectionName,
        query,
        20,
        searchOptions
      );
    } catch (error) {
      this.alertService.showErrorToast(translate('widgets.error-receiving-documents'), error);
      return;
    }

    const documentId = page.count ? page.items[0].id : null;
    if (documentId == null) {
      this.alertService.showErrorToast(translate('widgets.error-receiving-documents'));
      return;
    }

    const project = await this.projectService.getProjectById(this.dashboard.project_id, false);

    this.documentViewModalOptions = {
      collectionName: page.items[0].collectionName,
      page,
      pageIndex: 0,
      documentIndex: 0,
      limit: 20,
      documentId,
      projectId: this.dashboard.project_id,
      entity: this.pointName,
      entityDisplayName: this.pointName,
      analyticItem: this.payload.breakdownField,
      sentimentScale: this.dashboard.payload.sentimentScale,
      sentimentDisplay: this.dashboard.payload.sentimentDisplay,
      dashboardId: this.dashboard.id,
      isDashboardLocked: this.dashboard.locked,
      sentimentRanges: {
        feature: project.polarities.feature[this.dashboard.payload.sentimentScale],
        document: project.polarities.document[this.dashboard.payload.sentimentScale],
      },
      widgetDataSource: this.widgetDataSource,
    };
    this.showDocumentViewModal = true;
  }

  async exportAsImage(isPpt?: boolean): Promise<Blob> {
    const currentScrollTop = this.table.nativeElement.scrollTop;

    return toCanvas(this.table.nativeElement, { backgroundColor: 'white' }).then((canvas) => {
      if (!isPpt) {
        canvas = this.createNewCanvasWithTitle(canvas);
      }

      const contentDataURL = canvas.toDataURL('image/svg');

      const body = {
        filename: this.defaultTitle,
        svg: canvas,
      };

      this.table.nativeElement.scroll(0, currentScrollTop);

      return firstValueFrom(
        this.httpClient.post<Blob>(contentDataURL, body, {
          responseType: 'blob' as 'json',
        })
      );
    });
  }

  private dataSlideOptions() {
    const dataOptions = {};
    dataOptions['dataSource'] = this.widgetDataSource.displayName;
    dataOptions['docCount'] = this.filteredDocumentCount ?? this.widgetDataSource.documentCount;
    dataOptions['maxDocs'] = this.widgetDataSource.documentCount;
    return dataOptions;
  }

  async exportAsPptx(pptx: PptxReport): Promise<void> {
    this.addTitleSlideToPptx(pptx);

    const tableWidth = 12; // in inches
    let colW: any[] = this.dataSource.map(() => 1);

    const widthSum = colW.reduce((prev, curr) => prev + curr, 0);
    colW = colW.map((w) => ((w / widthSum) * tableWidth).toFixed(2));
    const dataOptions = this.dataSlideOptions();

    pptx.addTableSlide(this.generateTableForPptExport(), {
      newSlideStartY: 0.5,
      colW,
      w: tableWidth,
      h: 5.5,
      data: dataOptions,
      autoPage: true,
      lineWeight: -0.3,
      border: {
        color: 'black',
        pt: 2,
        type: 'solid',
      },
    });
  }

  generateTableForPptExport(): any {
    const pptTable = {
      rows: [],
    };

    pptTable.rows.push(
      this.displayedColumnsWithComment.map((key) => ({
        text: this.getColumnHeader(key),
        options: { bold: true, fontSize: 14 },
      }))
    );
    pptTable.rows = [
      ...pptTable.rows,
      ...this.dataSource.map((row: GridDataItem) => {
        const rowGroup = [];
        this.displayedColumnsWithComment.forEach((key) => {
          if (key !== 'comment') {
            rowGroup.push({ text: row[key] });
            return;
          }
          const comment = this.getCellComments(row.breakdown)?.find(
            (c) => c.summaryPinned || c.titlePinned
          );
          const textObj = {
            text: '',
          };
          if (comment?.titlePinned) {
            textObj.text = comment.title;
          }
          if (comment?.summaryPinned) {
            textObj.text = `${textObj.text}\n${comment.summary}`;
          }
          rowGroup.push(textObj);
        });
        return rowGroup;
      }),
    ];
    return pptTable;
  }

  async executeReportForCsv(_result: DownloadAsDialogResult) {
    const params = this.getParams();

    // set the breakdown label
    params['breakdownLabel'] = this.payload.breakdownLabel;

    const mapData = this.getMapData(true);
    if (!mapData.some((element) => element.customField === 'Documents')) {
      // if the report doesn't ask for a documents column, add one
      const docs = {
        label: translate('widgets.documents'),
        scoringStrategy: 'COUNT',
        customField: 'Documents',
      };
      mapData.splice(0, 0, docs);
    }

    // remove the hidden column containing the breakdown display values
    const breakdownValues = mapData.pop();
    if (this.payload.displayField !== this.payload.breakdownField) {
      // but if we're using a different display field then add it back in
      breakdownValues['label'] =
        FieldService.getFieldLongName(this.payload.displayField) || this.payload.displayField;
      mapData.splice(0, 0, breakdownValues);
    }

    const payload = {
      _type: TaskType.REPORT_EXTRACTOR_REPORT,
      filename: 'grid-report',
      source: this.dashboard.title,
      projectId: this.dashboard.project_id,
      collectionId: this.widgetDataSource.collectionName,
      analysisGroupId: this.widgetDataSource.groupId,
      reportName: 'documentNumericMetadata',
      query: this.filterService.getQueryForDocuments(this.widgetQueryOptions, false, true),
      params: encodeParams(params, this.widgetDataSource.groupId),
      mapData,
    };
    try {
      await this.downloadService.createTask(payload);
      this.alertService.showToast(
        'Your download is being generated.<br><a class="open-download-center-pointer">Click here</a> to view status.',
        undefined,
        undefined,
        undefined,
        'open-download-center-pointer',
        'open-download-center'
      );
    } catch (error) {
      this.alertService.showErrorToast(translate('widgets.failed-to-download'), error);
    }
    return undefined;
  }

  prepareDataForCsv(_data: any) {
    return;
  }

  public openSmartSummary() {
    const queryString = this.filterService.getQueryForDocuments(this.widgetQueryOptions);
    super.openSmartSummary(this.pointName, queryString);
  }

  async downloadSmartSummarySpotERequest() {
    this.downloadingSpoteRequest = true;
    this.downloadSmartSummaryRequestToolTip = translate(
      'widgets.smart-summary.currently-downloading-spote-request'
    );
    try {
      const queryString = this.filterService.getQueryForDocuments(this.widgetQueryOptions);
      const analyticItemType = this.payload.breakdownField;
      const spotERequest = await this.interactiveSmartSummaryService.getSpotEReqestForSummary(
        this.widgetDataSource.collectionName,
        this.widgetDataSource.groupId,
        this.widgetDataSource.analysisId,
        {
          paragraphLength: 1,
          analyticItemName: this.pointName,
          analyticItemType,
        },
        this.timezoneService.timezone,
        queryString,
        this.widget.payload.projectId
      );
      const jsonString = JSON.stringify(spotERequest);
      const filename = `${analyticItemType}_${this.pointName}_spote_request.json`;
      const blob = new Blob([jsonString], { type: 'application/json' });
      saveAs(blob, filename);
    } finally {
      this.downloadingSpoteRequest = false;
      this.downloadSmartSummaryRequestToolTip = '';
    }
  }

  openCellMenu($event, row: any, column: string, extraData): void {
    if (this.disablePinnedCommentClick) {
      return;
    }
    this.pointName = this.payload.transpose ? row.label : row[column];
    this.canAddFilterToWidgetGroup = !this.isFilterGroupParent && !this.dashboard.locked;
    this.canCreateSmartSummary = this.pointHasRequiredDocCountForSmartSummaries(row);
    if (this.isSmartSummaryEnabled) {
      this.interactiveSmartSummaryMenu = this.buildSmartSummaryMenu();
      this.removeInteractiveSmartSummaryMenu = this.buildSmartSummaryRemoveMenu();
    }
    super.openCellMenu($event, row, column, extraData);
  }

  getCellMenuData(row: any, column: any, extraData: any) {
    return {
      item: row[column],
      ...(this.payload.transpose && { item: `${row[column]}_${column}` }),
      itemDisplayName: extraData,
    };
  }

  onAddFiltersToWidgetGroup(result) {
    if (result) {
      result.filter = this.createFilter();

      const group = this.dashboardViewComponent
        .widgetGroups()
        .filter((g) => g.id === result['filterGroup'].id)[0];

      group.filters['metadata'][result['filterType'].id].push(result.filter);
      group.lastUsedDataSource = this.widgetDataSourceId;
      group.lastUsedDataSourceDisplayName = this.widgetDataSource.displayName;

      this.dashboardViewComponent.onUpdateWidgetGroup(group, undefined, undefined, true);
    }
    this.showAddToWidgetGroupModal = false;
  }

  onAddParentFilter(): void {
    const filter = this.createFilter();
    filter.parentFilter = true;

    this.widgetGroup.parentFilters = FilterService.getDefaultFilterSet();
    this.widgetGroup.parentFilters['metadata']['and'].push(filter);
    this.widgetGroup.lastUsedDataSource = this.widgetDataSourceId;
    this.widgetGroup.lastUsedDataSourceDisplayName = this.widgetDataSource.displayName;

    this.dashboardViewComponent.onUpdateWidgetGroup(
      this.widgetGroup,
      undefined,
      translate('widgets.parent-filter-set', { group: this.widgetGroup.title }),
      true
    );
  }

  private createFilter(): Filter {
    let filterValue = this.pointName;
    let displayName = this.pointName;
    const isBreakdownFieldStandard = FieldService.isStandardField(this.payload.breakdownField);

    if (FieldService.getFieldType(this.payload.analyticItem) === 'long') {
      filterValue = this.filterService.numericRangeModelToFilter({
        leftOperand: {
          operator: '=',
          value: this.pointName,
        },
      });
      displayName = this.filterService.translateKey(this.payload.analyticItem, filterValue);
    }

    return {
      analyticItem: `${!isBreakdownFieldStandard ? 'metadata.' : ''}${this.payload.breakdownField}`,
      value: this.pointName,
      displayName,
    };
  }

  onRemoveParentFilter(): void {
    this.widgetGroup.parentFilters = FilterService.getDefaultFilterSet();

    this.dashboardViewComponent.onUpdateWidgetGroup(
      this.widgetGroup,
      undefined,
      translate('widgets.parent-filter-removed', { group: this.widgetGroup.title }),
      true
    );
  }

  get widgetQueryOptions(): WidgetQueryOptions {
    const widgetQueryOptions = this.filterService.getWidgetQueryOptions(
      this.widget,
      this.widgetDataSource,
      this.widgetGroup,
      this.comparison?.filters[0]
    );

    widgetQueryOptions.pointName = !this.payload.transpose ? this.pointName : undefined;
    widgetQueryOptions.analyticItem = this.payload.breakdownField;
    widgetQueryOptions.analyticItems = ['metadata'];
    if (!FieldService.isCustomField(this.payload.breakdownField)) {
      widgetQueryOptions.analyticItem =
        FieldService.STANDARD_FIELDS_LABEL_TO_ID[this.payload.breakdownField] ||
        this.payload.breakdownField;
      widgetQueryOptions.analyticItems = ['standard Fields'];
    }

    // DASH-10000: If the grid widget has a date interval it means the breakdown is a date field.
    // In this case, we need to set the widgetContextMenu, so that the filter service will generate the correct query.
    if (widgetQueryOptions.dateInterval) {
      widgetQueryOptions.widgetContextMenu = {
        category: +new Date(widgetQueryOptions.pointName),
        index: this.dataSource.findIndex((item) => item.breakdown === widgetQueryOptions.pointName),
        series: {
          data: this.dataSource.map((item) => ({
            x: +new Date(item.breakdown),
          })),
        },
      };
    }

    return widgetQueryOptions;
  }

  get analyticItemFieldDetails() {
    return this.fieldDetailsMap.get(this.payload.breakdownField);
  }

  get hasTotalRow() {
    return this.payload.showAll || this.payload.showTopN;
  }

  itemToCommentAnalyticItem(item) {
    return [
      {
        name: item.item,
        displayName: item.item,
        type: this.widget.payload.analyticItem,
        subType: this.widget.payload.entityTypeFilter,
      },
    ];
  }

  itemToCommentPoint(item: any): CommentPoint {
    return {
      point: {
        x: 0,
        y: 0,
        xAxis: 0,
        yAxis: 0,
      },
      pointId: item.item,
    };
  }

  cellToPoint(row?: string, _column?: string) {
    return { item: row };
  }

  getCommentsForPoint(point: any) {
    return this.commentsByPointId().get(point.item);
  }

  getVisibleComments(): Comment[] {
    return super.getVisibleComments().filter((c) =>
      this.dataSource.some((d) => {
        if (!this.payload.transpose) {
          return d.breakdown === c.target.pointId;
        }
        return c.target.pointId === `${d.fieldId}_${Constants.TRANSPOSED_METADATA_COLUMN}`;
      })
    );
  }

  onCommentAdded(comment: Comment): void {
    super.onCommentAdded(comment);
    this.setColumns();
  }

  onCommentDeleted(comment: Comment): void {
    super.onCommentDeleted(comment);
    this.setColumns();
  }

  override onCellCommentIconClicked($event, row, column, extraData) {
    $event.stopPropagation();
    if (this.disablePinnedCommentClick) {
      return;
    }
    super.onCellCommentIconClicked($event, row, column, extraData);
  }

  private ensureSelectedComparisonMetadataField(): void {
    const count = this.payload.numericMetadataFields?.length ?? 0;
    if (!count) {
      this.payload.selectedComparisonMetadataFieldIndex = undefined;
      return;
    }
    const idx = this.payload.selectedComparisonMetadataFieldIndex;
    if (idx == null || idx < 0 || idx >= count) {
      this.payload.selectedComparisonMetadataFieldIndex = 0;
    }
  }
}
